import {
  type CaseDto,
  type ContractDto,
  type ContractListItemDto,
  type MeDto,
  type ReceivableDto, datePl } from '@bqcrm/shared';
import { Alert, Anchor, Badge, Button, Card, Checkbox, Group, Modal, Radio, Stack, Table, Text } from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../../api';
import { CompanyBadgeById, ContractStatusBadge, PaymentStatusBadge, TradeBadge } from '../../components/Badges';
import { Money } from '../../components/Money';
import { OnlineOverpayments } from '../../components/OnlineOverpayments';
import { QueryState } from '../../components/QueryState';
import { useCompanies, useTrades } from '../../data';
import { EmptyState } from '../../components/ui';
import { notifyError } from '../../notify';
import { ClientPaidButton, LastReminder, PaymentClaimNote, PaymentReminderButton, ReminderRequestButton } from '../../components/PaymentReminder';

/** „Przygotuj umowy”: domyślnie jedna umowa na firmę (wszystkie zaakceptowane zakresy), opcjonalnie osobno per branża (R5). */
function PrepareDialog({ c, contracts, onClose }: { c: CaseDto; contracts: ContractListItemDto[]; onClose: () => void }) {
  const trades = useTrades();
  const companies = useCompanies();
  const navigate = useNavigate();
  const covered = new Set(contracts.filter((k) => k.kind === 'umowa' && k.status !== 'anulowany').flatMap((k) => k.scopeIds));
  const candidates = c.scopes.filter((s) => !covered.has(s.id) && s.salesStage !== 'przegrany');
  const [selected, setSelected] = useState<string[]>(candidates.filter((s) => s.salesStage === 'zaakceptowany').map((s) => s.id));
  const [mode, setMode] = useState<'firma' | 'branza'>('firma');
  const groups =
    mode === 'firma'
      ? [...new Set(candidates.filter((s) => selected.includes(s.id)).map((s) => s.companyId))].map((cid) => candidates.filter((s) => s.companyId === cid && selected.includes(s.id)).map((s) => s.id))
      : selected.map((id) => [id]);
  const create = useMutation({
    mutationFn: async () => {
      const created: ContractDto[] = [];
      for (const scopeIds of groups) created.push(await api<ContractDto>('/contracts', { method: 'POST', body: { caseId: c.id, scopeIds } }));
      return created;
    },
    onSuccess: (created) => navigate(`/umowy/${created[0]!.id}`),
    onError: notifyError,
  });
  const tradeName = (code: string) => trades.data?.find((t) => t.code === code)?.name ?? code;

  return (
    <Stack>
      <Text size="sm">Umowa obejmuje zakresy jednej firmy — zakresy różnych firm to zawsze osobne umowy. Pozycje i ceny przechodzą z zaakceptowanych ofert.</Text>
      {candidates.map((s) => (
        <Checkbox
          key={s.id}
          checked={selected.includes(s.id)}
          onChange={(e) => {
            const on = e.currentTarget.checked;
            setSelected((x) => (on ? [...x, s.id] : x.filter((y) => y !== s.id)));
          }}
          label={
            <Group gap={6}>
              <TradeBadge code={s.tradeCode} />
              <Text size="sm">{tradeName(s.tradeCode)}</Text>
              <Text size="xs" c="dimmed">
                {companies.data?.find((co) => co.id === s.companyId)?.shortName}
              </Text>
              {s.salesStage !== 'zaakceptowany' && (
                <Badge size="xs" color="orange" variant="light">
                  bez akceptacji — pozycje wpiszesz ręcznie
                </Badge>
              )}
            </Group>
          }
        />
      ))}
      {!candidates.length && <Alert>Wszystkie zakresy są już objęte umowami.</Alert>}
      <Radio.Group value={mode} onChange={(v) => setMode(v as 'firma' | 'branza')} label="Podział na umowy">
        <Stack gap={4} mt={4}>
          <Radio value="firma" label="Jedna umowa na firmę (zalecane)" />
          <Radio value="branza" label="Osobna umowa dla każdej branży" />
        </Stack>
      </Radio.Group>
      <Text size="sm" c="dimmed">
        Powstanie umów: {groups.length}
      </Text>
      <Group justify="flex-end">
        <Button variant="default" onClick={onClose}>
          Anuluj
        </Button>
        <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!groups.length}>
          Przygotuj umowy
        </Button>
      </Group>
    </Stack>
  );
}

/**
 * newFor — z linku (?nowa-umowa=<zakres albo firma>): otwiera okno „Przygotuj umowy”; umowa powstaje dopiero po „Przygotuj”.
 * Gdy te zakresy ma już umowa robocza — przejście prosto do niej zamiast pustego okna.
 */
export function ContractsTab({ c, me, newFor, onNewHandled }: { c: CaseDto; me: MeDto; newFor?: string | null; onNewHandled?: () => void }) {
  const q = useQuery({ queryKey: ['case-contracts', c.id], queryFn: () => api<ContractListItemDto[]>(`/cases/${c.id}/contracts`) });
  const [open, setOpen] = useState(false);
  const canPrepare = c.can.prepareContract;
  const navigate = useNavigate();
  const contracts = q.data;
  useEffect(() => {
    if (!newFor || !contracts) return;
    onNewHandled?.();
    // newFor: zakres (podpowiedź „Co teraz?”) albo firma (zadanie „Przygotuj umowę”) — zaakceptowane zakresy bez wydanej / podpisanej umowy
    const active = (k: ContractListItemDto, statuses: string[]) => k.kind === 'umowa' && statuses.includes(k.status);
    const issued = new Set(contracts.filter((k) => active(k, ['wydany', 'podpisany'])).flatMap((k) => k.scopeIds));
    const target = c.scopes.filter((s) => (s.id === newFor || (s.companyId === newFor && s.salesStage === 'zaakceptowany')) && !issued.has(s.id));
    const drafts = contracts.filter((k) => active(k, ['roboczy']) && k.scopeIds.some((id) => target.some((s) => s.id === id)));
    const drafted = new Set(drafts.flatMap((k) => k.scopeIds));
    if (target.length && target.every((s) => drafted.has(s.id))) {
      // umowa robocza już jest — od razu do niej (kilka roboczych: zostaje lista w zakładce)
      if (drafts.length === 1) navigate(`/umowy/${drafts[0]!.id}`, { replace: true });
      return;
    }
    if (canPrepare) setOpen(true);
  }, [newFor, contracts, c.scopes, canPrepare, onNewHandled, navigate]);
  const companyIds = [...new Set((q.data ?? []).map((k) => k.companyId))];
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Text size="sm" c="dimmed" style={{ flex: '1 1 260px' }}>
          Umowa może być jedna na firmę albo osobna dla każdej branży. Zmiany po podpisaniu — aneksem.
        </Text>
        {canPrepare && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setOpen(true)}>
            Przygotuj umowy
          </Button>
        )}
      </Group>
      {!q.data?.length && (
        <EmptyState
          compact
          title="Brak umów"
          // P16: umowa jak oferta — dopiero po nadaniu biegu
          hint={canPrepare ? 'Umowy przygotujesz z zaakceptowanych ofert.' : !c.closedAt && c.intake.status !== 'zatwierdzona' ? 'Najpierw nadaj bieg sprawie — umowę przygotujesz po weryfikacji wstępnej.' : undefined}
        />
      )}
      <Stack>
        {companyIds.map((cid) => (
          <Card key={cid} withBorder padding="sm">
            <CompanyBadgeById id={cid} full />
            <Table.ScrollContainer minWidth={520} mt="xs">
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Umowa</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th ta="right">Wartość brutto</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(q.data ?? [])
                  .filter((k) => k.companyId === cid)
                  .map((k) => (
                    <Table.Tr key={k.id} style={{ opacity: k.status === 'anulowany' ? 0.5 : 1 }}>
                      <Table.Td>
                        <Anchor component={Link} to={`/umowy/${k.id}`} fw={600} size="sm">
                          {k.number ?? (k.kind === 'aneks' ? 'Aneks (roboczy)' : 'Umowa (robocza)')}
                        </Anchor>
                        <Group gap={4}>
                          {k.kind === 'aneks' && (
                            <Badge size="xs" variant="light">
                              aneks
                            </Badge>
                          )}
                          {k.basis === 'oferta' && (
                            <Badge size="xs" variant="light" color="teal">
                              zlecenie wg oferty
                            </Badge>
                          )}
                          {k.scopeIds.map((sid) => {
                            const s = c.scopes.find((x) => x.id === sid);
                            return s ? <TradeBadge key={sid} code={s.tradeCode} /> : null;
                          })}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        {k.basis === 'oferta' && k.status === 'podpisany' ? (
                          <Badge size="sm" variant="light" color="teal">
                            Bez umowy
                          </Badge>
                        ) : (
                          <ContractStatusBadge status={k.status} />
                        )}
                        {k.esignStage && (
                          <Text size="xs" c="dimmed">
                            {k.esignStage.waitingFor === 'firma' ? `e-podpis: czeka na podpis firmy${k.esignStage.firmSigner ? ` (${k.esignStage.firmSigner})` : ''}` : 'e-podpis: czeka na podpis klienta'}
                          </Text>
                        )}
                        {k.signedAt && (
                          <Text size="xs" c="dimmed">
                            {k.basis === 'oferta' ? 'od' : 'podpisana'} {datePl(k.signedAt)}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">{k.totalGrossGr !== null ? <Money gr={k.totalGrossGr} /> : '—'}</Table.Td>
                      <Table.Td>
                        {k.depositRequired && (
                          <Badge size="xs" color="orange" variant="light">
                            wymaga zaliczki
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
            </Table.ScrollContainer>
          </Card>
        ))}
      </Stack>
      <Modal opened={open} onClose={() => setOpen(false)} title="Przygotowanie umów" size="lg">
        {open && <PrepareDialog c={c} contracts={q.data ?? []} onClose={() => setOpen(false)} />}
      </Modal>
    </QueryState>
  );
}

/** Płatności sprawy — osobno dla każdej firmy (kwot dwóch podmiotów nie sumujemy). */
export function PaymentsTab({ c }: { c: CaseDto }) {
  const q = useQuery({ queryKey: ['case-receivables', c.id], queryFn: () => api<ReceivableDto[]>(`/finance/receivables?caseId=${c.id}`) });
  const companyIds = [...new Set((q.data ?? []).map((r) => r.companyId))];
  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      {!q.data?.length && <EmptyState compact title="Brak harmonogramów płatności" hint="Pojawią się po wydaniu umowy." />}
      <Stack>
        <OnlineOverpayments caseId={c.id} />
        {companyIds.map((cid) => {
          const rows = (q.data ?? []).filter((r) => r.companyId === cid);
          const total = rows.filter((r) => r.status !== 'anulowana').reduce((a, r) => a + r.amountGr, 0);
          const paid = rows.reduce((a, r) => a + r.paidGr, 0);
          return (
            <Card key={cid} withBorder padding="sm">
              <Group justify="space-between" wrap="wrap">
                <CompanyBadgeById id={cid} full />
                <Text size="sm">
                  wpłacono <Money gr={paid} /> z <Money gr={total} />
                </Text>
              </Group>
              <Table.ScrollContainer minWidth={520} mt="xs">
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Umowa / rata</Table.Th>
                    <Table.Th ta="right">Kwota brutto</Table.Th>
                    <Table.Th>Termin</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((r) => (
                    <Table.Tr key={r.itemId}>
                      <Table.Td>
                        <Anchor component={Link} to={`/umowy/${r.contractId}`} size="sm">
                          {r.contractNumber}
                        </Anchor>
                        <Text size="xs">{r.name}</Text>
                        {!r.contractSigned && (
                          <Badge size="xs" variant="light" color="gray">
                            umowa niepodpisana
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        <Money gr={r.amountGr} />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={r.overdue ? 'red' : undefined}>
                          {r.invoiceDue ? datePl(r.invoiceDue.date) : r.dueDate ? datePl(r.dueDate) : 'po zdarzeniu'}
                        </Text>
                        {r.invoiceDue && (
                          <Text size="xs" c="dimmed">
                            z faktury {r.invoiceDue.number ?? ''}
                            {r.dueDate && r.dueDate !== r.invoiceDue.date ? ` · harmonogram ${datePl(r.dueDate)}` : ''}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <PaymentStatusBadge status={r.status} overdue={r.overdue} />
                        <PaymentClaimNote claim={r.paymentClaim} />
                      </Table.Td>
                      <Table.Td>
                        {/* QA P24: po terminie — przypomnienie (księgowość) albo prośba do księgowości (opiekun) */}
                        {r.overdue && r.contractSigned && r.status !== 'oplacona' && r.status !== 'anulowana' && (r.canRemind ? <PaymentReminderButton itemId={r.itemId} /> : <ReminderRequestButton itemId={r.itemId} request={r.reminderRequest} lastReminder={r.lastReminder} />)}
                        {/* QA P21: ostatnie przypomnienie — opiekun widzi, że klientowi już przypomniano */}
                        <LastReminder r={r.lastReminder} />
                        {/* QA qa10311123 3.4: opiekun bez prawa księgowania — informacja od klienta dla księgowości */}
                        {!r.canRemind && r.contractSigned && (r.status === 'oczekuje' || r.status === 'czesciowo') && !r.paymentClaim && <ClientPaidButton itemId={r.itemId} remainingGr={r.amountGr - r.paidGr} />}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
              </Table.ScrollContainer>
            </Card>
          );
        })}
      </Stack>
    </QueryState>
  );
}
